import React from 'react';

import { classnames, useClassnames, visuallyHidden } from '@lumapps/classnames';
import { Icon, IconProps, Text, Tooltip, type TooltipProps } from '@lumapps/lumx/react';
import { useRovingTabIndex } from '@lumapps/moving-focus';
import { calculateThemeColor } from '@lumapps/utils/css/calculateThemeColor';
import { useOverflowTooltipLabel } from '@lumapps/utils/hooks/useOverflowTooltipLabel';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';
import type { PolymorphicComponentProps } from '@lumapps/utils/types/PolymorphicComponent';

import { BASE_CLASSNAME } from '../constants';
import { MenuContext } from '../context';

type LinkProps = { href?: string; target?: React.ComponentProps<'a'>['target'] };

type ButtonProps = { href?: never };

export interface BaseItemProps {
    /**
     * Forwarded class name
     */
    className?: string;
    /**
     * Mark menu item as disabled
     */
    isDisabled?: boolean;
    /**
     * Menu item icon
     */
    icon?: string;
    /**
     * A component to be rendered before the content.
     */
    before?: React.ReactNode;
    /**
     * A component to be rendered after the content.
     */
    after?: React.ReactNode;
    /**
     * A component to be rendered after the menu item (inside the <li>).
     */
    afterItem?: React.ReactNode;
    /**
     * Customize the menu item render component
     * (can be used to inject a custom router link)
     */
    as?: React.ElementType;
    /**
     * Menu item content (label)
     */
    children: React.ReactNode;
    /**
     * Options passed to useRovingTabIndex, useful for keyboard navigation
     */
    rovingTabIndexOptions?: {
        /**
         * An optional integer value that must be populated if the roving tabindex is being used in a grid. In that case,
         * set it to the zero-based index of the row that the given DOM element is currently part of. You can update this
         * row index as appropriate throughout the lifetime of the containing component, for example if the shape of the
         * grid can change dynamically.
         */
        rowIndex?: number | null;
    };
    /**
     * Add the label into a tooltip on menu item hover/focus
     */
    tooltipLabel?: string | false;
    /**
     * Forward tooltip props
     */
    tooltipProps?: Partial<TooltipProps>;
    /**
     * Forwarded styles
     */
    style?: React.CSSProperties;
    /**
     * On click callback
     */
    onClick?: React.MouseEventHandler;
    /**
     * Display variant
     * - 'button' display the before section, the label and the after section
     * - 'icon' displays only the before section (while keeping the label accessible)
     * @default 'button'
     */
    variant?: 'icon' | 'button';
}

export type MenuItemProps = BaseItemProps &
    (ButtonProps | LinkProps) & {
        color?: IconProps['color'];
        /** Whether the children wrapper should truncate. Defaults to true. */
        truncate?: boolean;
    };

export const CLASSNAME = `${BASE_CLASSNAME}-item`;

/**
 * Menu item (ARIA `menuitem`).
 *
 * To use inside a `Menu` or `Menubar`.
 *
 * @family Menus
 */
export const MenuItem = React.forwardRef(
    <E extends React.ElementType = 'button'>(
        props: PolymorphicComponentProps<E, MenuItemProps>,
        ref: React.Ref<HTMLElement>,
    ) => {
        const {
            className,
            children,
            before,
            after,
            afterItem,
            icon,
            as,
            onKeyDown,
            onClick,
            tooltipLabel,
            tooltipProps,
            'aria-haspopup': ariaHaspopup,
            'aria-busy': ariaBusy,
            'aria-checked': ariaChecked,
            'aria-expanded': ariaExpanded,
            'aria-disabled': ariaDisabled,
            isDisabled = ariaDisabled,
            role = 'menuitem',
            rovingTabIndexOptions,
            color,
            truncate = true,
            variant = 'button',
            ...forwardedProps
        } = props;
        const { block, element } = useClassnames(CLASSNAME);
        const wrapperRef = React.useRef<HTMLButtonElement>(null);
        const [tabIndex, focused, rovingTabindexKeyDown] = useRovingTabIndex(
            wrapperRef,
            ariaDisabled ? false : !!isDisabled,
            rovingTabIndexOptions?.rowIndex,
        );

        const { closeRoot, menuVariant, type: menuType } = React.useContext(MenuContext) || {};

        const handleKeyDown = React.useCallback<React.KeyboardEventHandler>(
            (event) => {
                rovingTabindexKeyDown(event);
                onKeyDown?.(event);
            },
            [rovingTabindexKeyDown, onKeyDown],
        );

        const handleClick = React.useCallback<React.MouseEventHandler>(
            (event) => {
                if (isDisabled || ariaDisabled) {
                    return;
                }
                // Close root menu if menuitem and not a submenu menu item and not busy
                if (role === 'menuitem' && !ariaHaspopup && !ariaBusy && ariaBusy !== 'true') {
                    // We need to delay closing the menu because it can cut redirections on touch devices
                    setTimeout(() => {
                        closeRoot?.();
                    }, 100);
                }

                onClick?.(event);
            },
            [ariaBusy, ariaDisabled, ariaHaspopup, closeRoot, isDisabled, onClick, role],
        );

        // Handle focus on roving tabindex update
        React.useEffect(() => {
            const wrapper = wrapperRef.current;
            if (wrapper && focused) {
                setTimeout(() => wrapper.focus(), 10);
            }
        }, [focused, tabIndex]);

        // Render button or link or custom component
        let wrapperProps: any = {};
        let WrapperElement = as || 'button';
        if (props.href && !as) {
            WrapperElement = 'a';
        }
        if (WrapperElement === 'button') {
            wrapperProps = { type: 'button', disabled: isDisabled && !ariaDisabled };
        }

        // Compute a tooltip label based on label content if text is overflowing
        const { labelRef, tooltipLabel: tooltipLabelSmall } = useOverflowTooltipLabel();
        /*
         * MenuItem in a menubar uses LumX Button styles
         * MenuItem in a menu with horizontal orientation uses LumX Button styles
         * MenuItem in a menu with vertical orientation uses LumX ListItem styles
         * @warning: Any changes to those rules should also be reflected in the .SCSS files.
         * When using Lumx ListItem we should also set --lumx-menu-divider-spacing: var(--lumx-spacing-unit-regular);
         * In order to set the divider spacing.

         *
         * We chosed to keep this two use-cases into a single component because,
         * other than style, they behave perfectly identically and we want to be able
         * to freely move MenuItem between menu and menubar.
         *
         * Here we are using the LumX classes directly because the component mixins
         * would not work correctly on some states (aria expanded, aria checked, etc.)
         */
        let lumxClasses: string | undefined;
        let lumxWrapperClasses: string | undefined;
        let lumxBeforeClasses: string | undefined;
        let lumxContentClasses = '';
        let lumxAfterClasses = '';
        if (menuVariant && (menuType === 'Menubar' || menuVariant?.orientation === 'horizontal')) {
            // Use LumX Button classes
            const { size = 'm', theme = 'light' } = menuVariant;
            lumxWrapperClasses = classnames(
                'lumx-button lumx-button--emphasis-low lumx-button--variant-button',
                isDisabled && 'lumx-button--is-disabled',
                ariaChecked && 'lumx-button--is-selected',
                ariaExpanded && 'lumx-button--is-hovered',
                `lumx-button--size-${size}`,
                `lumx-button--color-${calculateThemeColor(theme)}`,
            );
        } else {
            // Use LumX ListItem classes
            lumxClasses = 'lumx-list-item lumx-list-item--size-tiny';
            lumxWrapperClasses = classnames(
                'lumx-list-item__link',
                isDisabled && 'lumx-list-item__link--is-disabled',
                ariaChecked && 'lumx-list-item__link--is-selected',
                ariaExpanded && 'lumx-list-item__link--is-highlighted',
            );
            lumxBeforeClasses = 'lumx-list-item__before';
            lumxContentClasses = 'lumx-list-item__content';
            lumxAfterClasses = 'lumx-list-item__after';
        }

        // Hide label and after
        if (variant === 'icon') {
            lumxContentClasses += ` ${visuallyHidden()}`;
        }

        return (
            <li role="none" className={block([className, lumxClasses])}>
                <Tooltip label={tooltipLabel || tooltipLabelSmall} {...tooltipProps}>
                    <WrapperElement
                        ref={mergeRefs([ref, wrapperRef])}
                        role={role}
                        aria-haspopup={ariaHaspopup}
                        aria-busy={ariaBusy}
                        aria-disabled={isDisabled}
                        aria-checked={ariaChecked}
                        aria-expanded={ariaExpanded}
                        tabIndex={tabIndex}
                        className={element('wrapper', [lumxWrapperClasses])}
                        {...forwardedProps}
                        {...wrapperProps}
                        onKeyDown={handleKeyDown}
                        onClick={handleClick}
                    >
                        {(icon || before) && (
                            <Text as="span" className={element('before', [lumxBeforeClasses])}>
                                {icon && (
                                    <Icon
                                        className={element('before-icon')}
                                        icon={icon}
                                        size={menuVariant?.size === 's' ? 'xxs' : 'xs'}
                                        color={color}
                                    />
                                )}
                                {before}
                            </Text>
                        )}
                        <Text
                            ref={labelRef}
                            as="span"
                            truncate={truncate}
                            className={element('content', [lumxContentClasses])}
                            color={color}
                        >
                            {children}
                        </Text>
                        {after && (
                            <Text as="span" className={element('after', [lumxAfterClasses])}>
                                {after}
                            </Text>
                        )}
                    </WrapperElement>
                </Tooltip>
                {afterItem}
            </li>
        );
    },
);

MenuItem.displayName = 'MenuItem';
